// src/components/UsersList.js

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

const UsersList = () => {
  // All hooks at the top, before any return
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [updating, setUpdating] = useState({});
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [orgsModalOpen, setOrgsModalOpen] = useState(false);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedUserOrgs, setSelectedUserOrgs] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState('');

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (currentUser && !currentUser.isSuperAdmin) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  // All other useEffects and hooks go here (not after any return!)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, 'users');
        const userSnapshot = await getDocs(usersCollection);
        const usersData = userSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            appID: data.appID,
            ...data
          };
        });
        setUsers(usersData);
        setFilteredUsers(usersData);
        console.log("Fetched users:", usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    let filtered = users;
    if (filter === 'active') {
      filtered = users.filter((user) => user.status === 'Active');
    } else if (filter === 'inactive') {
      filtered = users.filter((user) => user.status === 'Inactive');
    }
    setFilteredUsers(filtered);
  }, [filter, users]);

  // Handler to toggle super admin
  const handleSuperAdminToggle = async (user) => {
    if (!currentUser?.isSuperAdmin) return;
    setUpdating((prev) => ({ ...prev, [user.id]: true }));
    try {
      await updateDoc(doc(db, 'users', user.id), {
        isSuperAdmin: !user.isSuperAdmin,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, isSuperAdmin: !user.isSuperAdmin } : u
        )
      );
    } catch (error) {
      console.error('Error updating super admin status:', error);
      alert('Failed to update super admin status.');
    } finally {
      setUpdating((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  // Handler to send password reset email
  const handleSendResetEmail = async (email) => {
    if (!email) {
      alert('No email address found for this user.');
      return;
    }
    try {
      await sendPasswordResetEmail(getAuth(), email);
      alert('Password reset email sent to ' + email);
    } catch (error) {
      alert('Failed to send reset email: ' + error.message);
    }
  };

  // Fetch organizations for a user, including org names
  const handleOrgsClick = async (user) => {
    setSelectedUserName(user.name || user.email || user.appID || 'User');
    setOrgsLoading(true);
    setOrgsModalOpen(true);
    try {
      const orgsRef = collection(db, 'users', user.id, 'organizations');
      const orgsSnap = await getDocs(orgsRef);
      const orgs = await Promise.all(orgsSnap.docs.map(async docSnap => {
        const orgId = docSnap.id;
        const orgData = docSnap.data();
        // Fetch org name from organizations collection
        let orgName = 'N/A';
        try {
          const orgDoc = await getDoc(doc(db, 'organizations', orgId));
          if (orgDoc.exists()) {
            orgName = orgDoc.data().name || orgId;
          }
        } catch {}
        return {
          id: orgId,
          name: orgName,
          isCreator: orgData.isCreator,
          isAdmin: orgData.isAdmin,
        };
      }));
      setSelectedUserOrgs(orgs);
    } catch (error) {
      setSelectedUserOrgs([]);
    } finally {
      setOrgsLoading(false);
    }
  };

  // Show loading spinner while currentUser is loading
  if (currentUser === undefined || currentUser === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }
  // If not super admin, return null (redirect handled by useEffect)
  if (!currentUser.isSuperAdmin) {
    return null;
  }

  return (
    <div>
      <div className="p-6 bg-background min-h-screen">
        <h1 className="text-3xl font-bold text-primary mb-6">User List</h1>
        <div className="flex items-center space-x-4 mb-6">
          <label htmlFor="status-filter" className="font-medium text-neutral">Filter:</label>
          <select
            id="status-filter"
            className="border border-gray-300 rounded-md px-4 py-2"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Users</option>
            <option value="active">Active Users</option>
            <option value="inactive">Inactive Users</option>
          </select>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
            <thead>
              <tr>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Name</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Email</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Orgs</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Status</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Groups</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Disciples</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Super Admin</th>
                <th className="px-4 py-2 border-b text-left font-semibold text-neutral">Reset Password</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-100 transition-colors">
                  <td className="px-4 py-2 border-b text-neutral">
                    <Link 
                      to={`/user/${user.appID || user.id}`}
                      className="text-primary hover:underline"
                    >
                      {user.name || 'N/A'}
                    </Link>
                  </td>
                  <td className="px-4 py-2 border-b text-neutral">{user.email || 'N/A'}</td>
                  <td className="px-4 py-2 border-b text-neutral cursor-pointer hover:underline hover:text-blue-600"
                      onClick={() => handleOrgsClick(user)}
                      title="View organization memberships"
                  >
                    <span className="text-blue-600 underline">list</span>
                  </td>
                  <td className="px-4 py-2 border-b text-neutral">
                    {user.status === 'Active' ? (
                      <span className="text-green-600">Active</span>
                    ) : (
                      <span className="text-red-600">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 border-b text-neutral">
                    {Array.isArray(user.groups) 
                      ? user.groups.join(', ') 
                      : 'N/A'}
                  </td>
                  <td className="px-4 py-2 border-b text-neutral">
                    {Array.isArray(user.discipleIds) ? user.discipleIds.length : 0}
                  </td>
                  <td className="px-4 py-2 border-b text-neutral text-center">
                    {currentUser?.isSuperAdmin ? (
                      <button
                        onClick={() => handleSuperAdminToggle(user)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${user.isSuperAdmin ? 'bg-green-200 border-green-500' : 'bg-gray-100 border-gray-300'} ${updating[user.id] ? 'opacity-50 cursor-wait' : ''}`}
                        disabled={updating[user.id]}
                        title={user.isSuperAdmin ? 'Revoke Super Admin' : 'Grant Super Admin'}
                      >
                        {updating[user.id] ? (
                          <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                        ) : user.isSuperAdmin ? (
                          <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        ) : (
                          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" /></svg>
                        )}
                      </button>
                    ) : (
                      user.isSuperAdmin ? <span className="text-green-600 font-semibold">Yes</span> : <span className="text-gray-400">No</span>
                    )}
                  </td>
                  <td className="px-4 py-2 border-b text-neutral text-center">
                    {currentUser?.isSuperAdmin && user.email ? (
                      <button
                        onClick={() => handleSendResetEmail(user.email)}
                        className="text-blue-600 hover:underline text-sm"
                        title="Send password reset email"
                      >
                        Send Reset
                      </button>
                    ) : (
                      <span className="text-gray-300">N/A</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Orgs Modal */}
      {orgsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl font-bold"
              onClick={() => setOrgsModalOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-xl font-bold mb-4">Organization Memberships for {selectedUserName}</h2>
            {orgsLoading ? (
              <div className="text-gray-500">Loading organizations...</div>
            ) : selectedUserOrgs.length === 0 ? (
              <div className="text-gray-400">No organization memberships found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 border-b text-left">Organization Name</th>
                      <th className="px-3 py-2 border-b text-left">Creator</th>
                      <th className="px-3 py-2 border-b text-left">Admin</th>
                      <th className="px-3 py-2 border-b text-left">Org ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedUserOrgs.map(org => (
                      <tr key={org.id}>
                        <td className="px-3 py-2 border-b">
                          <a
                            href={`/organization/${org.id}`}
                            className="text-blue-600 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {org.name || org.id}
                          </a>
                        </td>
                        <td className="px-3 py-2 border-b">{org.isCreator ? 'Y' : 'N'}</td>
                        <td className="px-3 py-2 border-b">{org.isAdmin ? 'Y' : 'N'}</td>
                        <td className="px-3 py-2 border-b">{org.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
